import { useCallback, useEffect, useState } from "react";
import { usePostHog } from "@posthog/react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { gigsApi } from "../../../lib/gigs";
import { mediaApi } from "../../../lib/media";
import {
  captureAdultGigCreated,
  type AdultGigCreateFlowAnalyticsContext,
  type AdultGigCreateFlowEntryPoint,
} from "../../../lib/analytics";
import type {
  AdultGigRequestListItemResponse,
  AdultGigRequestTeenDetailResponse,
  CreateGigRequest,
  GigFundingStatusResponse,
  GigListItemResponse,
  ScheduleProposalRequest,
} from "../../../types/gigTypes";
import { apiErrorToMessage } from "../../../utils/ApiUtils";
import { formatGigCompensation } from "../../../utils/GigCompensationUtils";
import { formatGigCrewSummary, getSpotsRemaining } from "../../../utils/GigCrewUtils";
import { formatCityStateZip } from "../../../utils/LocationUtils";
import CreateGigModal from "../../gigs/creategig";
import GigRequestConversationModal from "../../gigs/GigRequestConversationModal";
import { ScheduleProposalForm } from "../../gigs/ScheduleProposalModal";
import Button from "../../ui/Button/Button";
import Modal from "../../ui/Modal/Modal";
import PageActionBar from "../../ui/Page/PageActionBar";
import PageBackButton from "../../ui/Page/PageBackButton";
import PageHero from "../../ui/Page/PageHero";
import PageSectionCard from "../../ui/Page/PageSectionCard";
import PageShell from "../../ui/Page/PageShell";
import Toast, { type ToastTone } from "../../ui/Toast/Toast";
import TeenRequestProfileCard from "./TeenRequestProfileCard";
import useAdultGigEditor from "./useAdultGigEditor";
import { useAuth } from "../../../hooks/useAuth";
import { isBusinessAccount } from "../../../utils/AuthUtils";
import { formatDateRangeWithWeekdays, formatGigSchedule, getTeenName } from "./gigDisplay";
import {
  postedGigStatusClassName,
  postedGigStatusLabel,
  toReadableDate,
} from "../dashboard/shared/dashboardDisplay";

const ACTIVE_APPLICANT_STATUSES = new Set<AdultGigRequestListItemResponse["status"]>([
  "POSTER_PENDING",
  "POSTER_SELECTED_PENDING_CREW",
  "PAYOUT_SETUP_PENDING",
  "FUNDING_PENDING",
]);

const getGigReviewState = (requests: AdultGigRequestListItemResponse[]) => {
  const hasPosterPendingRequests = requests.some((request) => request.status === "POSTER_PENDING");
  const hasActiveApplicantRequests = requests.some((request) => ACTIVE_APPLICANT_STATUSES.has(request.status));
  const hasAcceptedRequest = requests.some((request) => request.status === "POSTER_ACCEPTED");

  return {
    hasPosterPendingRequests,
    hasPendingRequests: hasActiveApplicantRequests,
    hasAcceptedRequest,
  };
};

const hasExplicitOpenCapacity = (gig: GigListItemResponse) => {
  const hasCapacityFields =
    typeof gig.spotsRemaining === "number"
    || typeof gig.teensRequired === "number"
    || typeof gig.selectedSeatCount === "number"
    || typeof gig.confirmedSeatCount === "number";

  return hasCapacityFields && getSpotsRemaining(gig) > 0;
};

const isFilledOrClosedToApplicants = (gig: GigListItemResponse, hasAcceptedRequest: boolean) => {
  if (gig.status === "FILLED") {
    return true;
  }

  return hasAcceptedRequest && !hasExplicitOpenCapacity(gig);
};

type StatusFilter = "DRAFT" | "PUBLISHED" | "NEEDS_APPROVAL" | "EXPIRED";
type ToastState = { message: string; tone: ToastTone } | null;
type PendingFundingState = {
  gigId: string;
  gigTitle: string;
  compensationCents: number;
  request: AdultGigRequestListItemResponse;
};
type PendingVolunteerAcceptanceState = {
  gigId: string;
  gigTitle: string;
  request: AdultGigRequestListItemResponse;
};
type PendingRejectionState = {
  gigId: string;
  gigTitle: string;
  request: AdultGigRequestListItemResponse;
};
type StripeFundingReturnState = {
  result: string;
  paymentId: string | null;
};

const requiresFundingManualReview = (response: GigFundingStatusResponse) =>
  response.status === "FAILED" && response.fundedAt !== null && response.refundedAt === null;

const fundingStatusMessage = (response: GigFundingStatusResponse, returnResult?: string) => {
  if (requiresFundingManualReview(response)) {
    return "Stripe completed payment after this checkout was no longer valid. The crew was not confirmed, and MyTeenGig support will review the charge for refund.";
  }

  switch (response.status) {
    case "CHECKOUT_PENDING":
      return "Stripe checkout finished, but MyTeenGig is still waiting for the payment confirmation webhook.";
    case "FUNDED":
      return "The teen was accepted and the funds are now being held by MyTeenGig until the gig is completed.";
    case "RELEASE_READY":
      return "The gig is complete and the payout is queued, but Stripe still needs one more release step.";
    case "PAID_OUT":
      return "The payout has been released to the teen or guardian payout account.";
    case "REFUNDED":
      return "This payment was fully refunded to the adult account.";
    case "TRANSFER_REVERSED":
      return "The transfer was reversed before the final refund was processed.";
    case "FAILED":
      return returnResult === "cancel"
        ? "Funding checkout was canceled or is no longer active. No payment was completed."
        : "Stripe marked this funding attempt as failed.";
    default:
      return "Funding status updated.";
  }
};

const fundingStatusToastTone = (response: GigFundingStatusResponse): ToastTone => {
  if (requiresFundingManualReview(response)) {
    return "error";
  }
  if (response.status === "CHECKOUT_PENDING" || response.status === "REFUNDED" || response.status === "FAILED") {
    return "info";
  }
  return "success";
};

const isStatusFilter = (value: string | null): value is StatusFilter =>
  value === "DRAFT" || value === "PUBLISHED" || value === "NEEDS_APPROVAL" || value === "EXPIRED";

const isExpandedSection = (value: string | null): value is "teen" | "schedule" =>
  value === "teen" || value === "schedule";

const scrollToPostedGig = (gigId: string, attempt = 0) => {
  const targetElement = document.getElementById(`posted-gig-${gigId}`);
  if (!targetElement) {
    if (attempt < 8) {
      window.setTimeout(() => scrollToPostedGig(gigId, attempt + 1), 60);
    }
    return;
  }

  const topOffset = 96;
  const targetTop = targetElement.getBoundingClientRect().top + window.scrollY - topOffset;
  window.scrollTo({
    top: Math.max(0, targetTop),
    behavior: "smooth",
  });
};

const statusFilters: Array<{
  label: string;
  value: StatusFilter;
  helperText: string;
}> = [
  {
    label: "Review/Accept Applicants",
    value: "NEEDS_APPROVAL",
    helperText: "Published gigs with applicants currently waiting on your review or acceptance decision.",
  },
  {
    label: "Published",
    value: "PUBLISHED",
    helperText: "All live gigs that are still posted, including ones with applicants awaiting a decision.",
  },
  {
    label: "Expired",
    value: "EXPIRED",
    helperText: "Past gigs that were not filled and can be updated and reposted.",
  },
  {
    label: "Drafts",
    value: "DRAFT",
    helperText: "Gigs you still need to finish before publishing.",
  },
];

const AdultPostedGigs = () => {
  const posthog = usePostHog();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [gigs, setGigs] = useState<GigListItemResponse[]>([]);
  const [requestsByGigId, setRequestsByGigId] = useState<Record<string, AdultGigRequestListItemResponse[]>>({});
  const [expandedGigId, setExpandedGigId] = useState<string | null>(null);
  const [teenDetailsByRequestId, setTeenDetailsByRequestId] = useState<Record<string, AdultGigRequestTeenDetailResponse>>(
    {},
  );
  const [loadingTeenRequestId, setLoadingTeenRequestId] = useState<string | null>(null);
  const [approvingRequestId, setApprovingRequestId] = useState<string | null>(null);
  const [rejectingRequestId, setRejectingRequestId] = useState<string | null>(null);
  const [schedulingRequestId, setSchedulingRequestId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("PUBLISHED");
  const [toast, setToast] = useState<ToastState>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [createGigEntryPoint, setCreateGigEntryPoint] = useState<AdultGigCreateFlowEntryPoint>("posted_gigs_page_header");
  const [isSubmittingCreateGig, setIsSubmittingCreateGig] = useState(false);
  const [pendingCancelGig, setPendingCancelGig] = useState<GigListItemResponse | null>(null);
  const [cancellingGigId, setCancellingGigId] = useState<string | null>(null);
  const [isScheduleProposalComposerOpen, setIsScheduleProposalComposerOpen] = useState(false);
  const [scheduleProposalError, setScheduleProposalError] = useState<string | null>(null);
  const [pendingFunding, setPendingFunding] = useState<PendingFundingState | null>(null);
  const [pendingVolunteerAcceptance, setPendingVolunteerAcceptance] = useState<PendingVolunteerAcceptanceState | null>(null);
  const [pendingRejection, setPendingRejection] = useState<PendingRejectionState | null>(null);
  const [fundingCheckoutError, setFundingCheckoutError] = useState<string | null>(null);
  const [fundingAgreementAccepted, setFundingAgreementAccepted] = useState(false);
  const [isStartingFundingCheckout, setIsStartingFundingCheckout] = useState(false);
  const [stripeFundingReturn, setStripeFundingReturn] = useState<StripeFundingReturnState | null>(null);
  const [appliedNotificationTarget, setAppliedNotificationTarget] = useState<string | null>(null);
  const [scrollTargetGigId, setScrollTargetGigId] = useState<string | null>(null);
  const [selectedGigDetails, setSelectedGigDetails] = useState<GigListItemResponse | null>(null);
  const [selectedTeenRequest, setSelectedTeenRequest] = useState<AdultGigRequestListItemResponse | null>(null);
  const [selectedConversationRequest, setSelectedConversationRequest] = useState<AdultGigRequestListItemResponse | null>(null);
  const refreshPostedGigs = async () => {
    const postedGigs = await gigsApi.listAdultPostedGigs();
    setGigs(postedGigs);
    setExpandedGigId((currentExpandedGigId) => currentExpandedGigId ?? postedGigs[0]?.id ?? null);

    if (postedGigs.length === 0) {
      setRequestsByGigId({});
      return;
    }

    const requestEntries = await Promise.all(
      postedGigs.map(async (gig) => [gig.id, await gigsApi.listAdultRequestsForGig(gig.id)] as const),
    );
    setRequestsByGigId(Object.fromEntries(requestEntries));
  };

  const { modalProps, openEditor } = useAdultGigEditor({
    analyticsSource: "adult_posted_gigs",
    analyticsEntryPointByMode: {
      draft: "posted_gigs_draft_card",
      published: "posted_gigs_published_card",
      repost: "posted_gigs_expired_card",
    },
    onError: (message) => setError(message),
    onSuccess: async ({ mode, payload }) => {
      await refreshPostedGigs();

      if (payload.status === "DRAFT") {
        setStatusFilter("DRAFT");
        setToast({ tone: "success", message: "Draft saved." });
        return;
      }

      setStatusFilter("PUBLISHED");
      setToast({
        tone: "success",
        message: mode === "repost" ? "Gig updated and reposted." : mode === "published" ? "Gig updated." : "Gig published.",
      });
    },
  });

  const openCreateGigModal = (entryPoint: AdultGigCreateFlowEntryPoint) => {
    setCreateGigEntryPoint(entryPoint);
    setIsCreateModalOpen(true);
  };

  const handleCreateGig = async (
    payload: CreateGigRequest,
    images: File[],
    analyticsContext?: AdultGigCreateFlowAnalyticsContext,
  ) => {
    setIsSubmittingCreateGig(true);
    setToast(null);
    setError(null);

    try {
      const createdGig = await gigsApi.create(payload);
      captureAdultGigCreated(posthog, {
        gig_id: createdGig.id,
        status: payload.status === "DRAFT" ? "DRAFT" : "PUBLISHED",
        source: "adult_posted_gigs",
        ...(analyticsContext?.flow_id ? { flow_id: analyticsContext.flow_id } : {}),
        ...(analyticsContext?.entry_point ? { entry_point: analyticsContext.entry_point } : {}),
        ...(analyticsContext?.mode ? { mode: analyticsContext.mode } : {}),
        category: payload.category,
        volunteer: Boolean(payload.volunteer),
        schedule_type: payload.scheduleType ?? "FIXED",
        has_images: images.length > 0,
        image_count: images.length,
        teens_required: payload.teensRequired ?? 1,
        poster_payment_amount_cents: payload.paymentAmountCents,
      });

      let failedImageUploads = 0;
      for (const image of images) {
        try {
          await mediaApi.uploadFile(image, "GIG_IMAGE", createdGig.id);
        } catch {
          failedImageUploads += 1;
        }
      }

      await refreshPostedGigs();
      setIsCreateModalOpen(false);
      setStatusFilter(payload.status === "DRAFT" ? "DRAFT" : "PUBLISHED");

      if (failedImageUploads === 0) {
        setToast({
          tone: "success",
          message: payload.status === "DRAFT" ? "Gig saved as draft." : "Gig published.",
        });
        return;
      }

      setToast({
        tone: "error",
        message: `Gig created, but ${failedImageUploads} photo${failedImageUploads === 1 ? "" : "s"} failed to upload.`,
      });
    } catch (requestError) {
      throw new Error(apiErrorToMessage(requestError, "Failed to create gig."));
    } finally {
      setIsSubmittingCreateGig(false);
    }
  };

  useEffect(() => {
    let cancelled = false;

    const loadPostedGigs = async () => {
      setIsLoading(true);
      setError(null);

      try {
        await refreshPostedGigs();
        if (cancelled) {
          return;
        }
      } catch (requestError) {
        if (!cancelled) {
          setError(apiErrorToMessage(requestError, "Failed to load posted gigs."));
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    void loadPostedGigs();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const fundingStatus = searchParams.get("stripeFunding");
    const paymentId = searchParams.get("paymentId");
    if (!fundingStatus) {
      return;
    }

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("stripeFunding");
    nextParams.delete("paymentId");
    setSearchParams(nextParams, { replace: true });
    setStripeFundingReturn({ result: fundingStatus, paymentId });
  }, [searchParams, setSearchParams]);

  const loadTeenDetail = useCallback(async (gigId: string, requestId: string) => {
    if (teenDetailsByRequestId[requestId] || loadingTeenRequestId === requestId) {
      return;
    }

    setLoadingTeenRequestId(requestId);
    try {
      const teenDetail = await gigsApi.getAdultTeenDetailForGigRequest(gigId, requestId);
      setTeenDetailsByRequestId((current) => ({
        ...current,
        [requestId]: teenDetail,
      }));
    } catch (requestError) {
      setError(apiErrorToMessage(requestError, "Failed to load teen details."));
    } finally {
      setLoadingTeenRequestId((current) => (current === requestId ? null : current));
    }
  }, [loadingTeenRequestId, teenDetailsByRequestId]);

  useEffect(() => {
    const requestedGigId = searchParams.get("gigId");
    const targetRequestId = searchParams.get("requestId");
    const targetView = searchParams.get("view");
    const targetSection = searchParams.get("section");
    const resolvedGigId = requestedGigId ?? (
      targetRequestId
        ? Object.entries(requestsByGigId).find(([, requests]) => requests.some((request) => request.requestId === targetRequestId))?.[0] ?? null
        : null
    );
    const targetKey = [resolvedGigId ?? requestedGigId ?? "", targetRequestId ?? "", targetView ?? "", targetSection ?? ""].join("|");

    if ((!requestedGigId && !resolvedGigId) || targetKey === appliedNotificationTarget) {
      return;
    }

    if (targetView && isStatusFilter(targetView) && targetView !== statusFilter) {
      setStatusFilter(targetView);
    }

    const targetGigId = resolvedGigId ?? requestedGigId;
    if (!targetGigId) {
      return;
    }
    const nextExpandedSection = isExpandedSection(targetSection) ? targetSection : "teen";
    const gigExists = gigs.some((gig) => gig.id === targetGigId);
    const hasLoadedRequests = targetGigId ? Object.prototype.hasOwnProperty.call(requestsByGigId, targetGigId) : false;
    if (!gigExists || !hasLoadedRequests) {
      return;
    }

    setExpandedGigId(targetGigId);
    if (targetRequestId) {
      const request = requestsByGigId[targetGigId]?.find((currentRequest) => currentRequest.requestId === targetRequestId) ?? null;
      if (nextExpandedSection === "teen") {
        void loadTeenDetail(targetGigId, targetRequestId);
        setSelectedTeenRequest(request);
        setSelectedConversationRequest(null);
      } else {
        setSelectedConversationRequest(request);
        setSelectedTeenRequest(null);
      }
    }
    setScrollTargetGigId(targetGigId);

    setAppliedNotificationTarget(targetKey);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("gigId");
    nextParams.delete("requestId");
    nextParams.delete("view");
    nextParams.delete("section");
    setSearchParams(nextParams, { replace: true });
  }, [appliedNotificationTarget, gigs, loadTeenDetail, requestsByGigId, searchParams, setSearchParams, statusFilter]);

  useEffect(() => {
    if (!scrollTargetGigId || expandedGigId !== scrollTargetGigId) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      scrollToPostedGig(scrollTargetGigId);
      setScrollTargetGigId(null);
    }, 40);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [expandedGigId, scrollTargetGigId]);

  useEffect(() => {
    if (!stripeFundingReturn) {
      return;
    }

    if (stripeFundingReturn.result === "cancel" && !stripeFundingReturn.paymentId) {
      setToast({ tone: "info", message: "Funding checkout was canceled. No payment was completed." });
      setStripeFundingReturn(null);
    } else if (stripeFundingReturn.paymentId) {
      const paymentId = stripeFundingReturn.paymentId;
      const returnResult = stripeFundingReturn.result;
      let cancelled = false;
      let attempts = 0;

      const loadFundingStatus = async () => {
        try {
          const response = await gigsApi.getFundingStatus(paymentId);
          if (cancelled) {
            return;
          }

          if (response.status === "CHECKOUT_PENDING" && returnResult !== "cancel" && attempts < 4) {
            attempts += 1;
            window.setTimeout(() => {
              void loadFundingStatus();
            }, 2000);
            return;
          }

          await refreshPostedGigs();
          setToast({
            tone: fundingStatusToastTone(response),
            message: fundingStatusMessage(response, returnResult),
          });
          setStripeFundingReturn(null);
        } catch (requestError) {
          if (!cancelled) {
            setError(apiErrorToMessage(requestError, "Unable to load the Stripe funding status."));
            setStripeFundingReturn(null);
          }
        }
      };

      void loadFundingStatus();
      return () => {
        cancelled = true;
      };
    } else {
      setStripeFundingReturn(null);
    }
  }, [stripeFundingReturn]);

  useEffect(() => {
    if (selectedGigDetails) {
      const refreshedGig = gigs.find((gig) => gig.id === selectedGigDetails.id) ?? null;
      setSelectedGigDetails(refreshedGig);
    }

    if (selectedTeenRequest) {
      const refreshedRequest = Object.values(requestsByGigId)
        .flat()
        .find((request) => request.requestId === selectedTeenRequest.requestId) ?? null;
      setSelectedTeenRequest(refreshedRequest);
    }

    if (selectedConversationRequest) {
      const refreshedRequest = Object.values(requestsByGigId)
        .flat()
        .find((request) => request.requestId === selectedConversationRequest.requestId) ?? null;
      setSelectedConversationRequest(refreshedRequest);
    }
  }, [gigs, requestsByGigId, selectedConversationRequest, selectedGigDetails, selectedTeenRequest]);

  const handleConfirmRejection = async () => {
    if (!pendingRejection) {
      return;
    }

    setRejectingRequestId(pendingRejection.request.requestId);
    setToast(null);
    setError(null);

    try {
      await gigsApi.createPosterDecision(pendingRejection.request.requestId, {
        decision: "REJECTED",
        message: pendingRejection.request.message,
      });

      const [updatedGigs, updatedRequests] = await Promise.all([
        gigsApi.listAdultPostedGigs(),
        gigsApi.listAdultRequestsForGig(pendingRejection.gigId),
      ]);

      setGigs(updatedGigs);
      setRequestsByGigId((current) => ({
        ...current,
        [pendingRejection.gigId]: updatedRequests,
      }));
      setSelectedTeenRequest((current) => (
        current?.requestId === pendingRejection.request.requestId ? null : current
      ));
      setSelectedConversationRequest((current) => (
        current?.requestId === pendingRejection.request.requestId ? null : current
      ));
      setPendingRejection(null);
      setToast({ tone: "success", message: "Teen rejected for this gig." });
    } catch (requestError) {
      setError(apiErrorToMessage(requestError, "Failed to reject teen for this gig."));
    } finally {
      setRejectingRequestId(null);
    }
  };

  const handleConfirmVolunteerAcceptance = async () => {
    if (!pendingVolunteerAcceptance) {
      return;
    }

    setApprovingRequestId(pendingVolunteerAcceptance.request.requestId);
    setToast(null);
    setError(null);

    try {
      await gigsApi.createPosterDecision(pendingVolunteerAcceptance.request.requestId, {
        decision: "ACCEPTED",
        message: pendingVolunteerAcceptance.request.message,
        confirmedStartTime: pendingVolunteerAcceptance.request.proposedStartTime,
        confirmedEndTime: pendingVolunteerAcceptance.request.proposedEndTime,
      });

      const [updatedGigs, updatedRequests] = await Promise.all([
        gigsApi.listAdultPostedGigs(),
        gigsApi.listAdultRequestsForGig(pendingVolunteerAcceptance.gigId),
      ]);

      setGigs(updatedGigs);
      setRequestsByGigId((current) => ({
        ...current,
        [pendingVolunteerAcceptance.gigId]: updatedRequests,
      }));
      setExpandedGigId((currentExpandedGigId) => currentExpandedGigId ?? updatedGigs[0]?.id ?? null);
      setSelectedTeenRequest(null);
      setSelectedConversationRequest(null);
      setPendingVolunteerAcceptance(null);
      setToast({ tone: "success", message: "Teen accepted for this gig." });
    } catch (requestError) {
      setError(apiErrorToMessage(requestError, "Failed to accept teen for this gig."));
    } finally {
      setApprovingRequestId(null);
    }
  };

  const handleStartFundingCheckout = async () => {
    if (!pendingFunding) {
      return;
    }

    setIsStartingFundingCheckout(true);
    setToast(null);
    setError(null);
    setFundingCheckoutError(null);

    try {
      const response = await gigsApi.createFundingCheckoutSession(pendingFunding.request.requestId, {
        agreementAccepted: fundingAgreementAccepted,
        confirmedStartTime: pendingFunding.request.proposedStartTime,
        confirmedEndTime: pendingFunding.request.proposedEndTime,
      });
      if (!response.checkoutRequired) {
        await refreshPostedGigs();
        setPendingFunding(null);
        setToast({
          tone: "info",
          message: response.message ?? "Teen selected. Payment will be available after payout setup is complete.",
        });
        return;
      }
      if (!response.checkoutUrl) {
        setFundingCheckoutError("Stripe checkout is not available yet. Refresh this page and try again.");
        return;
      }
      setPendingFunding(null);
      window.location.assign(response.checkoutUrl);
    } catch (requestError) {
      setFundingCheckoutError(apiErrorToMessage(requestError, "Failed to start Stripe checkout for this gig."));
    } finally {
      setIsStartingFundingCheckout(false);
    }
  };

  const handleSubmitScheduleProposal = async (payload: ScheduleProposalRequest) => {
    if (!selectedConversationRequest?.gigId) {
      return;
    }

    const activeRequest = selectedConversationRequest;
    const gigId = activeRequest.gigId;
    if (!gigId) {
      return;
    }

    setSchedulingRequestId(activeRequest.requestId);
    setToast(null);
    setError(null);
    setScheduleProposalError(null);

    try {
      await gigsApi.submitAdultScheduleProposal(activeRequest.requestId, payload);

      const [updatedGigs, updatedRequests] = await Promise.all([
        gigsApi.listAdultPostedGigs(),
        gigsApi.listAdultRequestsForGig(gigId),
      ]);

      setGigs(updatedGigs);
      setRequestsByGigId((current) => ({
        ...current,
        [gigId]: updatedRequests,
      }));
      setSelectedConversationRequest(updatedRequests.find((request) => request.requestId === activeRequest.requestId) ?? activeRequest);
      setIsScheduleProposalComposerOpen(false);
      setToast({ tone: "success", message: "Updated proposed time sent to the teen." });
    } catch (requestError) {
      setScheduleProposalError(apiErrorToMessage(requestError, "Failed to send the updated proposed time."));
    } finally {
      setSchedulingRequestId(null);
    }
  };

  const handleConfirmCancelGig = async () => {
    if (!pendingCancelGig) {
      return;
    }

    const cancellationRefundsHeldFunds = pendingCancelGig.fundedOpen === true;
    setCancellingGigId(pendingCancelGig.id);
    setToast(null);
    setError(null);

    try {
      await gigsApi.cancelGig(pendingCancelGig.id);
      const updatedGigs = await gigsApi.listAdultPostedGigs();
      setGigs(updatedGigs);
      setExpandedGigId((currentExpandedGigId) => currentExpandedGigId ?? updatedGigs[0]?.id ?? null);
      setSelectedGigDetails(null);
      setSelectedTeenRequest(null);
      setSelectedConversationRequest(null);
      setPendingCancelGig(null);
      setToast({
        tone: "success",
        message: cancellationRefundsHeldFunds ? "Gig cancelled and refund started." : "Gig cancelled.",
      });
    } catch (requestError) {
      setError(apiErrorToMessage(requestError, "Failed to cancel gig."));
    } finally {
      setCancellingGigId(null);
    }
  };

  const filteredGigs = gigs.filter((gig) => {
    const requests = requestsByGigId[gig.id] ?? [];
    const { hasPendingRequests, hasAcceptedRequest } = getGigReviewState(requests);

    if (isFilledOrClosedToApplicants(gig, hasAcceptedRequest)) {
      return false;
    }

    if (statusFilter === "DRAFT") {
      return gig.status === "DRAFT";
    }

    if (statusFilter === "PUBLISHED") {
      return gig.status === "PUBLISHED";
    }

    if (statusFilter === "EXPIRED") {
      return gig.status === "EXPIRED";
    }

    return gig.status === "PUBLISHED" && hasPendingRequests;
  });

  const activeFilter = statusFilters.find((filter) => filter.value === statusFilter) ?? statusFilters[0];
  const businessMode = isBusinessAccount(user?.accountType);
  const pageTitle = businessMode ? "Job Listings" : "Posted Gigs";
  const createLabel = businessMode ? "Create a job listing" : "Post a gig";
  const hasAnyPostedGigs = gigs.length > 0;
  const emptyStateTitle = hasAnyPostedGigs
    ? businessMode ? "No listings in this view" : "No gigs in this view"
    : businessMode ? "Create your first job listing" : "Post your first gig";
  const emptyStateBody = hasAnyPostedGigs
    ? businessMode
      ? "Switch filters to review applicants waiting on your decision, all live listings, expired listings ready to repost, or drafts."
      : "Switch filters to review applicants waiting on your decision, all live published gigs, expired gigs ready to repost, or drafts."
    : businessMode
      ? "Create a listing so teens can discover the opportunity and apply."
      : "Create a gig so local teens can apply and you can choose who to hire.";
  const draftCount = gigs.filter((gig) => gig.status === "DRAFT").length;
  const publishedCount = gigs.filter((gig) => {
    const requests = requestsByGigId[gig.id] ?? [];
    const { hasAcceptedRequest } = getGigReviewState(requests);
    return gig.status === "PUBLISHED" && !isFilledOrClosedToApplicants(gig, hasAcceptedRequest);
  }).length;
  const needsReviewCount = gigs.filter((gig) => {
    const requests = requestsByGigId[gig.id] ?? [];
    const { hasPendingRequests, hasAcceptedRequest } = getGigReviewState(requests);
    return gig.status === "PUBLISHED" && hasPendingRequests && !isFilledOrClosedToApplicants(gig, hasAcceptedRequest);
  }).length;
  const expiredCount = gigs.filter((gig) => gig.status === "EXPIRED").length;

  return (
    <PageShell width="7xl">
      <Toast
        isOpen={toast !== null}
        tone={toast?.tone ?? "info"}
        message={toast?.message ?? ""}
        onClose={() => setToast(null)}
      />
      <PageBackButton fallbackTo="/dashboard" label="Back to Dashboard" className="mb-4" />
      <PageHero
        title={<span className="text-2xl sm:text-3xl">{pageTitle}</span>}
        className="bg-gradient-to-br from-white via-myteengig-background to-white py-3 sm:py-4 lg:py-4"
        contentClassName="[&_h1]:mt-0 [&_h1+div]:mt-3"
        footer={(
          <div className="flex justify-center">
            <Button
              type="button"
              size="sm"
              onClick={() => {
                setToast(null);
                setError(null);
                openCreateGigModal("posted_gigs_page_header");
              }}
              className="w-full sm:w-auto"
            >
              {createLabel}
            </Button>
          </div>
        )}
      />
      <div className="mt-8">
        <PageSectionCard className="bg-white/90 p-5">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center">
            <div className="lg:w-56 lg:shrink-0">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-myteengig-text-muted">
                {businessMode ? "Filter listings" : "Filter gigs"}
              </p>
              <p className="mt-1 max-w-sm text-xs leading-5 text-myteengig-text-muted lg:max-w-none">{activeFilter.helperText}</p>
            </div>
            <PageActionBar align="end" className="flex-1 sm:items-center lg:justify-end">
              {statusFilters.map((filter) => {
                const isActive = filter.value === statusFilter;
                const count =
                  filter.value === "DRAFT"
                    ? draftCount
                      : filter.value === "PUBLISHED"
                        ? publishedCount
                        : filter.value === "NEEDS_APPROVAL"
                          ? needsReviewCount
                          : expiredCount;

                return (
                  <button
                    key={filter.value}
                    type="button"
                    onClick={() => {
                      setStatusFilter(filter.value);
                      setExpandedGigId(null);
                      setSelectedGigDetails(null);
                      setSelectedTeenRequest(null);
                      setSelectedConversationRequest(null);
                      setToast(null);
                    }}
                    className={`rounded-full border px-3 py-2 text-sm font-semibold transition-colors xl:px-4 ${
                      isActive
                        ? "border-myteengig-primary bg-myteengig-primary text-white"
                        : "border-myteengig-border bg-white text-myteengig-text-primary hover:border-myteengig-primary hover:text-myteengig-primary"
                    }`}
                  >
                    <span className="inline-flex items-center gap-2">
                      <span>{filter.label}</span>
                      <span
                        className={`inline-flex min-w-7 items-center justify-center rounded-full px-2 py-0.5 text-xs font-bold ${
                          isActive
                            ? "bg-white/20 text-white"
                            : "bg-myteengig-background text-myteengig-text-primary"
                        }`}
                      >
                        {count}
                      </span>
                    </span>
                  </button>
                );
              })}
            </PageActionBar>
          </div>
        </PageSectionCard>

        {isLoading ? <p className="mt-6 text-myteengig-text-muted">Loading your posted gigs...</p> : null}
        {error ? <p className="mt-6 text-sm font-medium text-red-600">{error}</p> : null}

        {!isLoading && !error && filteredGigs.length === 0 ? (
          <PageSectionCard className="mt-8 border-dashed bg-white/80 p-8 text-center">
            <h2 className="text-xl font-semibold text-myteengig-text-primary">{emptyStateTitle}</h2>
            <p className="mt-2 text-sm text-myteengig-text-muted">{emptyStateBody}</p>
            {!hasAnyPostedGigs ? (
              <Button
                type="button"
                className="mt-5 w-full sm:w-auto"
                onClick={() => {
                  setToast(null);
                  setError(null);
                  openCreateGigModal("posted_gigs_empty_state");
                }}
              >
                {businessMode ? "Create your first listing" : "Post your first gig"}
              </Button>
            ) : null}
          </PageSectionCard>
        ) : null}

        {!isLoading && !error && filteredGigs.length > 0 ? (
          <div className="mt-8 grid gap-6">
            {filteredGigs.map((gig) => {
              const requests = requestsByGigId[gig.id] ?? [];
              const hasLoadedRequests = Object.prototype.hasOwnProperty.call(requestsByGigId, gig.id);
              const { hasPendingRequests } = getGigReviewState(requests);
              const canReviewApplicants = gig.status === "PUBLISHED" && hasPendingRequests;
              const canContinueDraft = gig.status === "DRAFT";
              const hasActiveRequests = requests.some(
                (request) => request.status === "GUARDIAN_PENDING" || ACTIVE_APPLICANT_STATUSES.has(request.status),
              );
              const canEditPublished = gig.status === "PUBLISHED" && hasLoadedRequests;
              const canRepost = gig.status === "EXPIRED";
              const canCancel = gig.status === "DRAFT" || gig.status === "PUBLISHED" || gig.status === "EXPIRED";
              const pendingApplicantCount = requests.filter(
                (request) => ACTIVE_APPLICANT_STATUSES.has(request.status),
              ).length;

              return (
                <section
                  key={gig.id}
                  id={`posted-gig-${gig.id}`}
                  className="overflow-hidden rounded-[1.75rem] border border-myteengig-border bg-white shadow-sm"
                >
                  <div className="border-b border-myteengig-border bg-[radial-gradient(circle_at_top_left,_rgba(163,230,53,0.18),_transparent_38%),linear-gradient(135deg,_rgba(255,255,255,1),_rgba(249,250,251,1))] p-6">
                    <div className="flex flex-col gap-5">
                      <div className="max-w-5xl">
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-3">
                              <h2 className="text-2xl font-semibold text-myteengig-text-primary">{gig.title}</h2>
                              <span
                                className={`rounded-full border px-3 py-1 text-xs font-semibold ${postedGigStatusClassName[gig.status]}`}
                              >
                                {postedGigStatusLabel[gig.status]}
                              </span>
                              <span className="rounded-full border border-myteengig-border bg-white/90 px-3 py-1 text-sm font-semibold text-myteengig-text-primary shadow-sm">
                                {formatGigCompensation(gig.paymentAmountCents, gig.volunteer)}
                              </span>
                              <span className="rounded-full border border-myteengig-border bg-white/90 px-3 py-1 text-sm font-semibold text-myteengig-text-primary shadow-sm">
                                {formatGigCrewSummary(gig)}
                              </span>
                              {gig.fundedOpen ? (
                                <span className="rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-900">
                                  Payment held
                                </span>
                              ) : null}
                            </div>
                          </div>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="w-full sm:w-auto"
                            onClick={() => setSelectedGigDetails(gig)}
                          >
                            View details
                          </Button>
	                        </div>
	                        <p className="mt-3 text-sm leading-6 text-myteengig-text-muted">{gig.description}</p>
	                        {gig.requiresEquipment ? (
	                          <p className="mt-3 inline-flex rounded-full border border-myteengig-border bg-white/90 px-3 py-1 text-xs font-semibold text-myteengig-text-primary shadow-sm">
	                            Equipment required
	                          </p>
	                        ) : null}
	                        <p className="mt-4 text-xs font-medium uppercase tracking-wide text-myteengig-text-muted">
	                          Posted {toReadableDate(gig.createdAt)}
                        </p>
                      </div>

                      {canContinueDraft || canEditPublished || canRepost ? (
                        <div className="flex flex-col gap-5 lg:items-center">
                          {canContinueDraft ? (
                            <div className="w-full max-w-md rounded-3xl border border-myteengig-border bg-white/90 p-4">
                              <p className="text-center text-xs font-semibold uppercase tracking-[0.18em] text-myteengig-text-muted">
                                Continue drafting
                              </p>
                              <p className="mt-3 text-center text-sm text-myteengig-text-muted">
                                Reopen this draft to update details, save again, or publish it.
                              </p>
                              <button
                                type="button"
                                onClick={() => {
                                  setToast(null);
                                  setError(null);
                                  void openEditor(gig.id, "draft");
                                }}
                                className="mt-4 inline-flex w-full items-center justify-center rounded-full bg-myteengig-primary px-4 py-3 text-sm font-semibold text-white transition-opacity hover:opacity-90"
                              >
                                Continue draft
                              </button>
                            </div>
                          ) : null}

                          {canEditPublished ? (
                            <div className="flex w-full justify-start">
                              <button
                                type="button"
                                onClick={() => {
                                  setToast(null);
                                  setError(null);
                                  void openEditor(gig.id, "published", { hasActiveRequests });
                                }}
                                className="inline-flex w-full items-center justify-center rounded-full border border-myteengig-border bg-white px-4 py-3 text-sm font-semibold text-myteengig-text-primary transition-colors hover:bg-myteengig-background sm:w-auto"
                              >
                                Edit Gig
                              </button>
                            </div>
                          ) : null}

                          {canRepost ? (
                            <div className="w-full max-w-md rounded-3xl border border-myteengig-border bg-white/90 p-4">
                              <p className="text-center text-xs font-semibold uppercase tracking-[0.18em] text-myteengig-text-muted">
                                Repost this gig
                              </p>
                              <p className="mt-3 text-center text-sm text-myteengig-text-muted">
                                Update the time window or details, then put this gig back live.
                              </p>
                              <button
                                type="button"
                                onClick={() => {
                                  setToast(null);
                                  setError(null);
                                  void openEditor(gig.id, "repost");
                                }}
                                className="mt-4 inline-flex w-full items-center justify-center rounded-full bg-myteengig-primary px-4 py-3 text-sm font-semibold text-white transition-opacity hover:opacity-90"
                              >
                                Edit and repost
                              </button>
                            </div>
                          ) : null}
                        </div>
                      ) : null}

                      {canCancel || canReviewApplicants ? (
                        <div className="grid gap-5 lg:grid-cols-2 lg:items-end">
                          <div className="flex w-full justify-start">
                            {canCancel ? (
                              <button
                                type="button"
                                disabled={cancellingGigId === gig.id}
                                onClick={() => setPendingCancelGig(gig)}
                                className="inline-flex w-full items-center justify-center rounded-full border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-700 transition-colors hover:bg-rose-100 disabled:cursor-not-allowed disabled:opacity-70 sm:w-auto"
                              >
                                {cancellingGigId === gig.id
                                  ? "Cancelling..."
                                  : gig.fundedOpen
                                    ? "Cancel and refund"
                                    : "Cancel gig"}
                              </button>
                            ) : null}
                          </div>

                          <div className="flex w-full justify-start lg:justify-end">
                            {canReviewApplicants ? (
                              <button
                                type="button"
                                onClick={() => navigate(`/adult/gigs/${gig.id}/applicants`)}
                                className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-myteengig-primary px-4 py-3 text-sm font-semibold text-white transition-opacity hover:opacity-90 sm:w-auto"
                              >
                                <span>View applicants</span>
                                <span className="rounded-full bg-white/18 px-2 py-0.5 text-xs font-semibold">
                                  {pendingApplicantCount}
                                </span>
                              </button>
                            ) : null}
                          </div>
                        </div>
                      ) : null}
                    </div>
                  </div>
                </section>
              );
            })}
          </div>
        ) : null}
      </div>

      <CreateGigModal
        isOpen={isCreateModalOpen}
        isSubmitting={isSubmittingCreateGig}
        onClose={() => {
          if (!isSubmittingCreateGig) {
            setIsCreateModalOpen(false);
          }
        }}
        onSubmit={handleCreateGig}
        modalTitle={businessMode ? "Create Job Listing" : undefined}
        publishLabel={businessMode ? "Publish Listing" : undefined}
        publishingLabel={businessMode ? "Publishing Listing..." : undefined}
        draftLabel={businessMode ? "Save Draft" : undefined}
        draftingLabel={businessMode ? "Saving Draft..." : undefined}
        analyticsSource="adult_posted_gigs"
        analyticsEntryPoint={createGigEntryPoint}
        analyticsMode="create"
      />

      <CreateGigModal
        {...modalProps}
      />

      <Modal
        isOpen={selectedGigDetails !== null}
        onClose={() => setSelectedGigDetails(null)}
        title={selectedGigDetails?.title ?? "Gig Details"}
        panelClassName="max-w-5xl"
      >
        {selectedGigDetails ? (
          <div className="grid gap-4 text-sm text-myteengig-text-primary sm:grid-cols-2">
	            <div className="rounded-2xl bg-myteengig-background px-4 py-4">
	              <p className="text-xs uppercase tracking-wide text-myteengig-text-muted">Description</p>
	              <p className="mt-2 whitespace-pre-wrap leading-6">{selectedGigDetails.description}</p>
	            </div>
	            {selectedGigDetails.requiresEquipment ? (
	              <div className="rounded-2xl bg-myteengig-background px-4 py-4">
	                <p className="text-xs uppercase tracking-wide text-myteengig-text-muted">Equipment or supplies</p>
	                <p className="mt-2 whitespace-pre-wrap leading-6">
	                  {selectedGigDetails.equipmentDescription?.trim() || "Equipment is required, but details were not provided."}
	                </p>
	              </div>
	            ) : null}
	            <div className="rounded-2xl bg-myteengig-background px-4 py-4">
	              <p className="text-xs uppercase tracking-wide text-myteengig-text-muted">Schedule</p>
              <p className="mt-2 font-medium">{formatGigSchedule(selectedGigDetails)}</p>
            </div>
            <div className="rounded-2xl bg-myteengig-background px-4 py-4">
              <p className="text-xs uppercase tracking-wide text-myteengig-text-muted">Category</p>
              <p className="mt-2 font-medium">{selectedGigDetails.category}</p>
            </div>
            <div className="rounded-2xl bg-myteengig-background px-4 py-4">
              <p className="text-xs uppercase tracking-wide text-myteengig-text-muted">Location</p>
              <p className="mt-2 font-medium">{formatCityStateZip(selectedGigDetails)}</p>
            </div>
            <div className="rounded-2xl bg-myteengig-background px-4 py-4">
              <p className="text-xs uppercase tracking-wide text-myteengig-text-muted">Pay</p>
              <p className="mt-2 font-medium">{formatGigCompensation(selectedGigDetails.paymentAmountCents, selectedGigDetails.volunteer)}</p>
            </div>
            <div className="rounded-2xl bg-myteengig-background px-4 py-4">
              <p className="text-xs uppercase tracking-wide text-myteengig-text-muted">Crew</p>
              <p className="mt-2 font-medium">{formatGigCrewSummary(selectedGigDetails)}</p>
            </div>
            <div className="rounded-2xl bg-myteengig-background px-4 py-4">
              <p className="text-xs uppercase tracking-wide text-myteengig-text-muted">Estimated duration</p>
              <p className="mt-2 font-medium">
                {selectedGigDetails.estimatedDurationMinutes
                  ? `${selectedGigDetails.estimatedDurationMinutes} minutes`
                  : "Not set"}
              </p>
            </div>
          </div>
        ) : null}
      </Modal>

      <Modal
        isOpen={selectedTeenRequest !== null}
        onClose={() => setSelectedTeenRequest(null)}
        title={selectedTeenRequest ? getTeenName(selectedTeenRequest) : "Teen"}
        panelClassName="max-w-4xl"
      >
        {selectedTeenRequest ? (
          <div className="space-y-4">
            <div className="rounded-2xl bg-myteengig-background px-4 py-4">
              <p className="text-sm text-myteengig-text-muted">
                Review the applicant profile, their note, and the time they proposed before making your decision.
              </p>
            </div>
            <TeenRequestProfileCard
              teenName={getTeenName(selectedTeenRequest)}
              teenDetail={teenDetailsByRequestId[selectedTeenRequest.requestId]}
            />
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="rounded-2xl bg-myteengig-background px-4 py-4">
                <p className="text-xs uppercase tracking-wide text-myteengig-text-muted">Teen message</p>
                <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-myteengig-text-primary">
                  {selectedTeenRequest.message?.trim() || "No message included."}
                </p>
              </div>
              <div className="rounded-2xl bg-myteengig-background px-4 py-4">
                <p className="text-xs uppercase tracking-wide text-myteengig-text-muted">Requested schedule</p>
                <p className="mt-2 text-sm font-medium text-myteengig-text-primary">
                  {formatDateRangeWithWeekdays(selectedTeenRequest.proposedStartTime, selectedTeenRequest.proposedEndTime, selectedTeenRequest.gigTimeZone)}
                </p>
              </div>
            </div>
          </div>
        ) : null}
      </Modal>

      <GigRequestConversationModal
        isOpen={selectedConversationRequest !== null}
        onClose={() => {
          setSelectedConversationRequest(null);
          setIsScheduleProposalComposerOpen(false);
          setScheduleProposalError(null);
        }}
        title={selectedConversationRequest ? `${getTeenName(selectedConversationRequest)} · Message / Suggest Time` : "Message / Suggest Time"}
        role="adult"
        requestId={selectedConversationRequest?.requestId ?? ""}
        participantLabel="teen"
        perspective="adult"
        proposals={selectedConversationRequest?.scheduleProposals}
        timeZone={selectedConversationRequest?.gigTimeZone}
        summary={selectedConversationRequest ? (
          <div className="space-y-2">
            <p className="text-sm font-semibold text-myteengig-text-primary">{getTeenName(selectedConversationRequest)}</p>
            <p className="text-sm text-myteengig-text-muted">
              Message this applicant and work through timing before you decide whether to accept them.
            </p>
            {selectedConversationRequest.teenConfirmedPosterTimeAt ? (
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
                This teen confirmed your latest suggested start time and is waiting on your decision.
              </div>
            ) : null}
          </div>
        ) : null}
        scheduleProposalContent={selectedConversationRequest && isScheduleProposalComposerOpen ? (
          <ScheduleProposalForm
            subtitle="Pick a new start time and MyTeenGig will keep the same gig length for the updated proposal."
            initialStartTime={selectedConversationRequest.proposedStartTime ?? null}
            initialEndTime={selectedConversationRequest.proposedEndTime ?? null}
            availabilityStartTime={selectedConversationRequest.gigStartTime ?? null}
            availabilityEndTime={selectedConversationRequest.gigEndTime ?? null}
            timeZone={selectedConversationRequest.gigTimeZone}
            allowOutsideAvailabilityWindow
            submitError={scheduleProposalError}
            isSubmitting={schedulingRequestId === selectedConversationRequest.requestId}
            onCancel={() => {
              setIsScheduleProposalComposerOpen(false);
              setScheduleProposalError(null);
            }}
            onSubmit={handleSubmitScheduleProposal}
          />
        ) : null}
        composerSecondaryAction={selectedConversationRequest ? (
          <Button
            variant="outline"
            disabled={schedulingRequestId === selectedConversationRequest.requestId}
            onClick={() => {
              setIsScheduleProposalComposerOpen((current) => !current);
              setScheduleProposalError(null);
            }}
          >
            {schedulingRequestId === selectedConversationRequest.requestId
              ? "Sending..."
              : isScheduleProposalComposerOpen
                ? "Hide Start Time Picker"
                : "Suggest New Start Time"}
          </Button>
        ) : null}
        emptyThreadMessage="No messages or timing updates yet."
        messagesDescription="Messages and timing changes stay together in this applicant thread."
      />

      <Modal
        isOpen={pendingCancelGig !== null}
        onClose={() => (cancellingGigId ? null : setPendingCancelGig(null))}
        title={pendingCancelGig?.fundedOpen ? "Cancel and Refund Gig" : "Cancel Gig"}
        panelClassName="max-w-lg"
      >
        <div className="space-y-4">
          <p className="text-sm text-myteengig-text-muted">
            {pendingCancelGig?.fundedOpen ? (
              <>
                Cancel <span className="font-semibold text-myteengig-text-primary">{pendingCancelGig.title}</span> and refund
                the held payment? This will move it out of your active workflow, stop new activity on it, and refund your held payment.
              </>
            ) : (
              <>
                Cancel <span className="font-semibold text-myteengig-text-primary">{pendingCancelGig?.title}</span>? This
                will move it out of your active workflow and stop new activity on it.
              </>
            )}
          </p>
          <PageActionBar align="end">
            <Button variant="outline" onClick={() => setPendingCancelGig(null)} disabled={cancellingGigId !== null} className="w-full sm:w-auto">
              Keep gig
            </Button>
            <Button onClick={() => void handleConfirmCancelGig()} disabled={cancellingGigId !== null} className="w-full sm:w-auto">
              {cancellingGigId !== null
                ? "Cancelling..."
                : pendingCancelGig?.fundedOpen
                  ? "Cancel and refund"
                  : "Cancel gig"}
            </Button>
          </PageActionBar>
        </div>
      </Modal>

      <Modal
        isOpen={pendingRejection !== null}
        onClose={() => {
          if (rejectingRequestId === null) {
            setPendingRejection(null);
          }
        }}
        title="Reject Teen?"
        panelClassName="max-w-lg"
      >
        {pendingRejection ? (
          <div className="space-y-4">
            <div className="rounded-2xl bg-myteengig-background px-4 py-4">
              <p className="text-sm font-semibold text-myteengig-text-primary">
                {getTeenName(pendingRejection.request)}
              </p>
              <p className="mt-2 text-sm text-myteengig-text-muted">
                This will mark the request as declined and remove this teen from the active applicant list for {pendingRejection.gigTitle}.
              </p>
            </div>

            <p className="text-sm text-myteengig-text-muted">Are you sure you want to reject this teen?</p>

            <div className="flex flex-wrap justify-end gap-3">
              <Button
                variant="outline"
                disabled={rejectingRequestId !== null}
                onClick={() => setPendingRejection(null)}
                className="w-full sm:w-auto"
              >
                Keep Applicant
              </Button>
              <Button
                disabled={rejectingRequestId !== null}
                onClick={() => void handleConfirmRejection()}
                className="w-full sm:w-auto"
              >
                {rejectingRequestId !== null ? "Rejecting..." : "Yes, Reject Teen"}
              </Button>
            </div>
          </div>
        ) : null}
      </Modal>

      <Modal
        isOpen={pendingFunding !== null}
        onClose={() => {
          if (isStartingFundingCheckout) {
            return null;
          }
          setPendingFunding(null);
          setFundingCheckoutError(null);
        }}
        title={(
          <span className="flex flex-wrap items-center gap-3">
            <span>
              {pendingFunding?.request.status === "POSTER_PENDING" && !pendingFunding.request.teenPayoutReady
                ? "Select Pending Payout Setup"
                : pendingFunding?.request.status === "FUNDING_PENDING"
                  ? "Pay to Confirm Gig"
                  : "Accept and Fund Gig"}
            </span>
            {pendingFunding ? (
              <span className="rounded-full border border-myteengig-border bg-white px-3 py-1 text-sm font-semibold text-myteengig-text-primary shadow-sm">
                {formatGigCompensation(pendingFunding.compensationCents, false)}
              </span>
            ) : null}
          </span>
        )}
        panelClassName="max-w-2xl"
      >
        {pendingFunding ? (
          <div className="space-y-5">
            <div className="space-y-2 text-sm text-myteengig-text-muted">
              <p>
                {pendingFunding.request.status === "POSTER_PENDING" && !pendingFunding.request.teenPayoutReady ? (
                  <>
                    You can select <span className="font-semibold text-myteengig-text-primary">{pendingFunding.request.teenFirstName?.trim() || "this teen"}</span>{" "}
                    now, but this gig will not be fully confirmed or funded until their payout setup is complete.
                  </>
                ) : (
                  <>
                    By accepting, you agree to pay <span className="font-semibold text-myteengig-text-primary">{pendingFunding.request.teenFirstName?.trim() || "this teen"}</span>{" "}
                    <span className="font-semibold text-myteengig-text-primary">{formatGigCompensation(pendingFunding.compensationCents, false)}</span> when{" "}
                    <span className="font-semibold text-myteengig-text-primary">{pendingFunding.gigTitle}</span> is marked complete by you.
                  </>
                )}
              </p>
            </div>

            <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
              <p className="text-sm font-semibold text-amber-900">
                {pendingFunding.request.status === "POSTER_PENDING" && !pendingFunding.request.teenPayoutReady
                  ? "No payment is collected yet"
                  : "Funds are held until completion"}
              </p>
              <p className="mt-2 text-sm text-amber-900/90">
                {pendingFunding.request.status === "POSTER_PENDING" && !pendingFunding.request.teenPayoutReady
                  ? "MyTeenGig will notify you when this teen's payout setup is ready so you can return and pay to fully confirm the gig."
                  : "Stripe checkout authorizes and funds the gig now, but the teen will not receive the payout until the gig is marked complete by you."}
              </p>
            </div>

            <div className="rounded-2xl border border-myteengig-border p-4">
              <p className="text-sm font-semibold text-myteengig-text-primary">Planned time window</p>
              <p className="mt-2 text-sm text-myteengig-text-muted">
                {formatDateRangeWithWeekdays(pendingFunding.request.proposedStartTime, pendingFunding.request.proposedEndTime, pendingFunding.request.gigTimeZone)}
              </p>
            </div>

            <div className="rounded-2xl border border-myteengig-border p-4">
              <p className="text-sm font-semibold text-myteengig-text-primary">What happens next</p>
              <div className="mt-2 space-y-2 text-sm text-myteengig-text-muted">
                {pendingFunding.request.status === "POSTER_PENDING" && !pendingFunding.request.teenPayoutReady ? (
                  <>
                    <p>You have not paid yet. You can wait for this teen to finish payout setup or choose another applicant who is ready now.</p>
                    <p>After payout setup is ready, use Pay to confirm to move the gig into scheduled gigs.</p>
                  </>
                ) : (
                  <>
                    <p>Once payment is confirmed, this gig will move into your upcoming scheduled gigs.</p>
                    <p>The payment stays held in MyTeenGig until the scheduled gig is marked complete.</p>
                    <p>{pendingFunding.request.teenFirstName?.trim() || "The teen"} will then be expected to come during the confirmed time window shown above.</p>
                  </>
                )}
              </div>
            </div>

            <div className="rounded-2xl border border-myteengig-border p-4">
              <p className="text-sm font-semibold text-myteengig-text-primary">Agreement</p>
              <p className="mt-2 text-sm text-myteengig-text-muted">
                {pendingFunding.request.status === "POSTER_PENDING" && !pendingFunding.request.teenPayoutReady
                  ? "By continuing, you agree that this teen is conditionally selected and no payment will be collected until payout setup is complete and you return to pay."
                  : "By continuing, you agree that MyTeenGig will collect and hold this payment until the scheduled gig is marked complete."}
              </p>
              <label className="mt-4 flex items-start gap-3 rounded-2xl bg-myteengig-background p-4">
                <input
                  type="checkbox"
                  checked={fundingAgreementAccepted}
                  onChange={(event) => setFundingAgreementAccepted(event.target.checked)}
                  className="mt-1 h-4 w-4 rounded border-myteengig-border"
                />
                <span className="text-sm text-myteengig-text-primary">
                  {pendingFunding.request.status === "POSTER_PENDING" && !pendingFunding.request.teenPayoutReady
                    ? "I understand this selection is conditional, no payment is collected now, and the request is accepted only after payout setup is ready and checkout succeeds."
                    : "I agree to the funding terms and understand the request is accepted only after successful checkout, and the teen is paid only after the gig is marked complete."}
                </span>
              </label>
            </div>

            {fundingCheckoutError ? (
              <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3">
                <p className="text-sm font-medium text-red-700">{fundingCheckoutError}</p>
              </div>
            ) : null}

            <PageActionBar align="between">
              <Button
                variant="outline"
                onClick={() => {
                  setPendingFunding(null);
                  setFundingCheckoutError(null);
                }}
                disabled={isStartingFundingCheckout}
                className="w-full sm:w-auto"
              >
                Not now
              </Button>
              <Button disabled={!fundingAgreementAccepted || isStartingFundingCheckout} onClick={() => void handleStartFundingCheckout()} className="w-full sm:w-auto">
                {isStartingFundingCheckout
                  ? pendingFunding.request.status === "POSTER_PENDING" && !pendingFunding.request.teenPayoutReady
                    ? "Selecting..."
                    : "Redirecting..."
                  : pendingFunding.request.status === "POSTER_PENDING" && !pendingFunding.request.teenPayoutReady
                    ? "Select teen"
                    : "Continue to Stripe"}
              </Button>
            </PageActionBar>
          </div>
        ) : null}
      </Modal>

      <Modal
        isOpen={pendingVolunteerAcceptance !== null}
        onClose={() => {
          if (approvingRequestId) {
            return null;
          }
          setPendingVolunteerAcceptance(null);
        }}
        title="Accept Teen"
        panelClassName="max-w-2xl"
      >
        {pendingVolunteerAcceptance ? (
          <div className="space-y-5">
            <p className="text-sm text-myteengig-text-muted">
              You are about to accept <span className="font-semibold text-myteengig-text-primary">{pendingVolunteerAcceptance.request.teenFirstName?.trim() || "this teen"}</span> for your gig.
            </p>

            <div className="rounded-2xl border border-myteengig-border p-4">
              <p className="text-sm font-semibold text-myteengig-text-primary">What happens next</p>
              <div className="mt-3 space-y-2 text-sm text-myteengig-text-muted">
                <p>The teen will be officially accepted for this gig right away.</p>
                <p>The latest proposed time on this request will become the confirmed schedule for the gig.</p>
                <p>This gig will move out of your posted gigs and into scheduled gigs.</p>
                <p>Any other open requests for this gig will be closed because the position is being filled.</p>
                <p>No payment will be collected because this gig is currently marked as a volunteer opportunity.</p>
              </div>
            </div>

            <PageActionBar align="between">
              <Button
                variant="outline"
                onClick={() => setPendingVolunteerAcceptance(null)}
                disabled={approvingRequestId !== null}
                className="w-full sm:w-auto"
              >
                Not now
              </Button>
              <Button
                onClick={() => void handleConfirmVolunteerAcceptance()}
                disabled={approvingRequestId !== null}
                className="w-full sm:w-auto"
              >
                {approvingRequestId !== null ? "Accepting..." : "Accept teen"}
              </Button>
            </PageActionBar>
          </div>
        ) : null}
      </Modal>
    </PageShell>
  );
};

export default AdultPostedGigs;
